// Some browsers expose the Storage API but throw a SecurityError as soon as
// the property is read (for example, sandboxed frames or blocked storage).
// Keep storage an optional enhancement so those contexts can still render.

const memoryStores = {
  localStorage: new Map(),
  sessionStorage: new Map()
};

function getBrowserStorage(type) {
  try {
    if (typeof window === 'undefined') return null;
    return window[type] || null;
  } catch {
    return null;
  }
}

function createSafeStorage(type) {
  const memory = memoryStores[type];

  return {
    getItem(key) {
      const storage = getBrowserStorage(type);

      if (storage) {
        try {
          return storage.getItem(key);
        } catch {
          // Fall through to the per-page in-memory store.
        }
      }

      return memory.has(key) ? memory.get(key) : null;
    },

    setItem(key, value) {
      const stringValue = String(value);
      const storage = getBrowserStorage(type);

      if (storage) {
        try {
          storage.setItem(key, stringValue);
          return;
        } catch {
          // Preserve the value for this page when persistence is blocked.
        }
      }

      memory.set(key, stringValue);
    },

    removeItem(key) {
      const storage = getBrowserStorage(type);

      if (storage) {
        try {
          storage.removeItem(key);
        } catch {
          // Also clear any value written to the fallback store.
        }
      }

      memory.delete(key);
    },

    clear() {
      const storage = getBrowserStorage(type);

      if (storage) {
        try {
          storage.clear();
        } catch {
          // Also clear the fallback store.
        }
      }

      memory.clear();
    }
  };
}

export const safeLocalStorage = createSafeStorage('localStorage');
export const safeSessionStorage = createSafeStorage('sessionStorage');

export const getNativeLocalStorage = () => getBrowserStorage('localStorage');
